import _ from 'underscore';

const tokenPattern = /[:{](\w+[.\w]+)}?/;
const gTokenPattern = new RegExp(tokenPattern.source, 'g');

// null/undefined check
function isValue(value) {
  return typeof value !== 'undefined' && value !== null;
}

function haveBlankVal(data) {
  let vals;
  if (Array.isArray(data)) {
    vals = data;
  } else if (_.isObject(data)) {
    vals = Object.values(data);
  } else {
    vals = [data];
  }
  return vals.some((v) => !isValue(v));
}

function countTokens(template) {
  const matches = template.match(gTokenPattern);
  if (matches) {
    return matches.length;
  }
  return 0;
}

function replaceTokens(template, data) {
  // replace tokens using a bare string or array of args
  if (Array.isArray(data)) {
    const d = [...data];
    while (d.length) {
      // eslint-disable-next-line no-param-reassign
      template = template.replace(tokenPattern, String(d.shift()));
    }
    return template;
  }

  // replace using object key/vals
  return template.replace(gTokenPattern, (match, key) => {
    const value = _.isObject(data) ? data[key] : data;
    return isValue(value) ? value : '';
  });
}

/**
 * ApiCall wraps a single call to a single endpoint and handles the entire
 * request/response lifecycle.
 */
export class ApiCall {
  method = 'get';
  url = null;
  tokens = 0;
  opts = {};
  axios = null;
  model = null;
  ser = true;
  deser = true;
  before = [];
  after = [];

  // Whether or not to unwrap the response data.
  // must be true if deser is true.
  // This flag is processed by ApiResponsePlugin
  unwrap = true;

  constructor(url, data) {
    Object.assign(this, data);
    // support shortened method+url syntax
    // e.g., "POST campaigns"
    const matches = url.trim().match(/^(?:(get|post|patch|put|delete|head)\s+)(\S+)$/i);
    if (matches && matches.length > 0) {
      [, this.method, this.url] = matches;
    } else {
      this.url = url;
    }

    this.tokens = countTokens(this.url);

    if (this.model) {
      if (this.serde) {
        delete this.serde;
        this.ser = true;
        this.deser = true;
      }
    } else {
      this.ser = false;
      this.deser = false;
    }

    if (!this.before) {
      this.before = [];
    }
    if (!this.after) {
      this.after = [];
    }

    if (this.plugins) {
      this.withPlugins(...this.plugins);
      delete this.plugins;
    }
  }

  withModel(model) {
    this.model = model;
    return this;
  }

  withPlugins(...plugins) {
    if (!plugins) {
      return this;
    }
    plugins.forEach((p) => {
      this.before.push(p.before);
      this.after.push(p.after);
    });
    plugins.forEach((p) => {
      // initialize the plugin state after setting up before/after hooks
      //
      // this allows us to do a little trickery, specifically in the
      // DupeRequestPlugin which needs to run it's before hook first but it's
      // after hook last
      p.init.call(this);
    });
    return this;
  }

  /**
   *  Execute the API call and return a Promise
   *
   * @param {Object, Array<String>} pathArgs Arguments to substitute into the URL path
   * @param {Object} data to send with request as query params or body
   * @param {Object} opts Extra call-specific options to modify this request/behavior
   *
   * @returns {Promise}
   */
  exec(pathArgs, data, opts) {
    if (this.tokens === 0 && (!data || !opts)) {
      // no token replacement needed, assume data & opts passed in
      // eslint-disable-next-line no-param-reassign
      [opts, data] = [data, pathArgs];
    }

    // sanity check
    const method = this.method.toLowerCase();
    if (typeof this.axios[method] !== 'function') {
      throw new Error(`No such HTTP method '${method}'`);
    }

    if (method === 'delete') {
      // Check for null/undefined values when deleting
      //
      // Allows for a slightly cleaner API when trying to delete models which
      // have not actually been saved. Instead of writing an extra method for
      // each delete call which checks the values and then returns a promise, we
      // can just short-circuit it here.
      if (haveBlankVal(pathArgs)) {
        return Promise.resolve();
      }
    }

    // setup config object
    // ApiCall opts + opts passed in + method/url/data
    const reqConfig = { ...this.opts, ...opts };
    reqConfig.method = method;
    reqConfig.url = this.createUrl(pathArgs);

    // add data as either query params or put/post body
    if (data) {
      if (method === 'get' && _.isObject(data) && !reqConfig.url.includes('?')) {
        reqConfig.params = data;
      } else {
        reqConfig.data = data;
      }
    }

    // process before hooks
    for (let i = 0; i < this.before.length; i++) {
      const fn = this.before[i];
      const result = fn.call(this, reqConfig);
      if (result && result.done === true) {
        // short-circuit the execution and return a value directly
        return result.value;
      }
    }

    // make request
    return this.axios
      .request(reqConfig)
      .then((res) => {
        // run after hooks, modifying result
        this.after.forEach((fn) => {
          const result = fn.call(this, res, reqConfig);
          if (typeof result !== 'undefined') {
            // eslint-disable-next-line no-param-reassign
            res = result;
          }
        });
        return res;
      })
      .catch((error) => {
        // notify plugins of the error
        this.after.forEach((fn) => {
          fn.call(this, null, reqConfig, error);
        });
        return Promise.reject(error);
      });
  }

  createUrl(pathArgs) {
    return replaceTokens(this.url, pathArgs);
  }

  // create and return a lambda which executes the ApiCall
  create() {
    const apiCall = this;
    return (pathArgs, data, opts) => apiCall.exec.call(apiCall, pathArgs, data, opts);
  }
}
